import React, { useState } from 'react';
import api from '../utils/api';
import { saveBlob } from '../utils/download';

const TEXT = {
    title: 'כלי דיבאג למנהל',
    loading: 'טוען...',
    storageTitle: 'בדיקת אחסון העלאות',
    storageDescription: 'בדיקה שהשרת כותב לנתיב ההעלאות ומחובר לכונן האחסון.',
    storageAction: 'בדוק אחסון',
    storageError: 'לא ניתן לבדוק את האחסון.',
    ok: 'תקין',
    fail: 'שגיאה',
    templateTitle: 'כלים לתבניות',
    templateDescription: 'פעולות אדמין מאובטחות עבור תבניות הטפסים.',
    convertAction: 'המר כל שדות מספר לשדות טקסט',
    convertConfirm: 'להמיר כעת את כל שדות המספר בכל התבניות?',
    convertError: 'לא ניתן להמיר את השדות.',
    convertSuccess: 'שדות עודכנו:',
    autoFontAction: 'העבר את כל השדות למצב גודל גופן אוטומטי',
    autoFontConfirm: 'להעביר עכשיו את כל השדות הנתמכים למצב גודל גופן אוטומטי?',
    autoFontError: 'לא ניתן לעדכן את מצב גודל הגופן.',
    autoFontSuccess: 'שדות שעודכנו לגודל גופן אוטומטי:',
    exportAction: 'הורד את כל JSON-י התבניות',
    exportError: 'לא ניתן להוריד את יצוא התבניות.',
    uploadRoot: 'נתיב אחסון',
    uploadProfile: 'תמונות פרופיל',
    uploadTemplates: 'קבצי תבניות',
};

const MANUAL_TEXT = {
    title: 'מדריך משתמש',
    description: 'העלה קובץ PDF והוא יישמר לנתיב שממנו עמוד מדריך המשתמש קורא בשרת.',
    file: 'קובץ PDF',
    action: 'העלה מדריך',
    success: 'המדריך נשמר בהצלחה:',
    error: 'לא ניתן להעלות את מדריך המשתמש.',
    missingFile: 'יש לבחור קובץ PDF.',
};

const TEMPLATE_FILES_TEXT = {
    title: 'Template PDF files',
    description: 'Checks whether the template PDFs referenced by the database exist on the server volume.',
    action: 'Check template files',
    missingCount: 'Missing template PDFs',
    allFound: 'All template PDFs were found.',
    upload: 'Upload replacement PDF',
    choose: 'Choose PDF',
    missingFile: 'Choose a replacement PDF first.',
    success: 'Template PDF saved:',
    error: 'Unable to repair template PDF.',
    loadError: 'Unable to load template file status.',
};

const EMAIL_TEXT = {
    title: 'Email outbox',
    description: 'Recent queued email jobs and the last worker error, if any.',
    action: 'Refresh email jobs',
    retry: 'Retry failed email',
    error: 'Unable to load email jobs.',
    retryError: 'Unable to retry email job.',
    empty: 'No email jobs found.',
};

const GLOBAL_PASSWORD_TEXT = {
    title: 'סיסמה גלובלית למשתמשים',
    description: 'מעדכן את הסיסמה לכל המשתמשים שאינם מנהלים. חשבונות מנהל לא ישתנו.',
    password: 'סיסמה גלובלית חדשה',
    confirmPassword: 'אימות סיסמה',
    action: 'עדכן לכל המשתמשים',
    confirm: 'לעדכן את הסיסמה לכל המשתמשים שאינם מנהלים?',
    mismatch: 'אימות הסיסמה אינו תואם.',
    required: 'חובה להזין סיסמה.',
    success: 'משתמשים שעודכנו:',
    error: 'לא ניתן לעדכן את הסיסמה הגלובלית.',
};

function parseFilename(disposition) {
    const match = /filename="?([^";]+)"?/i.exec(disposition || '');
    return match?.[1] || 'pdf-template-export.json';
}

function renderTextWithLinks(value) {
    const text = String(value || '');
    if (!text) return null;
    const urlPattern = /(https?:\/\/[^\s<>"']+)/g;
    const parts = text.split(urlPattern);
    return parts.map((part, index) => {
        if (!part) return null;
        if (urlPattern.test(part)) {
            urlPattern.lastIndex = 0;
            return (
                <a key={`${part}-${index}`} href={part} target="_blank" rel="noreferrer">
                    {part}
                </a>
            );
        }
        urlPattern.lastIndex = 0;
        return <React.Fragment key={`${index}-${part.slice(0, 8)}`}>{part}</React.Fragment>;
    });
}

export default function AdminDebug() {
    const [storageHealth, setStorageHealth] = useState(null);
    const [storageLoading, setStorageLoading] = useState(false);
    const [storageError, setStorageError] = useState('');
    const [convertLoading, setConvertLoading] = useState(false);
    const [convertError, setConvertError] = useState('');
    const [convertResult, setConvertResult] = useState(null);
    const [autoFontLoading, setAutoFontLoading] = useState(false);
    const [autoFontError, setAutoFontError] = useState('');
    const [autoFontResult, setAutoFontResult] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState('');
    const [manualFile, setManualFile] = useState(null);
    const [manualLoading, setManualLoading] = useState(false);
    const [manualError, setManualError] = useState('');
    const [manualResult, setManualResult] = useState(null);
    const [templateFileReport, setTemplateFileReport] = useState(null);
    const [templateFilesLoading, setTemplateFilesLoading] = useState(false);
    const [templateFilesError, setTemplateFilesError] = useState('');
    const [templateRepairFiles, setTemplateRepairFiles] = useState({});
    const [templateRepairLoadingId, setTemplateRepairLoadingId] = useState('');
    const [templateRepairError, setTemplateRepairError] = useState('');
    const [templateRepairResult, setTemplateRepairResult] = useState(null);
    const [emailJobs, setEmailJobs] = useState([]);
    const [emailJobsLoading, setEmailJobsLoading] = useState(false);
    const [emailJobsError, setEmailJobsError] = useState('');
    const [emailRetryingId, setEmailRetryingId] = useState('');
    const [globalPassword, setGlobalPassword] = useState('');
    const [globalPasswordConfirm, setGlobalPasswordConfirm] = useState('');
    const [globalPasswordLoading, setGlobalPasswordLoading] = useState(false);
    const [globalPasswordError, setGlobalPasswordError] = useState('');
    const [globalPasswordResult, setGlobalPasswordResult] = useState(null);

    const handleStorageCheck = async () => {
        try {
            setStorageLoading(true);
            setStorageError('');
            const { data } = await api.get('/admin/uploads-health');
            setStorageHealth(data);
        } catch (err) {
            setStorageError(TEXT.storageError);
            setStorageHealth(null);
        } finally {
            setStorageLoading(false);
        }
    };

    const handleConvertFields = async () => {
        if (!window.confirm(TEXT.convertConfirm)) return;
        try {
            setConvertLoading(true);
            setConvertError('');
            setAutoFontResult(null);
            const { data } = await api.post('/admin/debug/template-fields/number-to-text');
            setConvertResult(data);
        } catch (err) {
            setConvertError(TEXT.convertError);
            setConvertResult(null);
        } finally {
            setConvertLoading(false);
        }
    };

    const handleConvertFieldsToAutoFont = async () => {
        if (!window.confirm(TEXT.autoFontConfirm)) return;
        try {
            setAutoFontLoading(true);
            setAutoFontError('');
            setConvertResult(null);
            const { data } = await api.post('/admin/debug/template-fields/auto-font-size');
            setAutoFontResult(data);
        } catch (err) {
            setAutoFontError(TEXT.autoFontError);
            setAutoFontResult(null);
        } finally {
            setAutoFontLoading(false);
        }
    };

    const handleDownloadExports = async () => {
        try {
            setExportLoading(true);
            setExportError('');
            const response = await api.get('/admin/debug/template-exports', {
                responseType: 'blob',
            });
            saveBlob(
                response.data,
                parseFilename(response.headers?.['content-disposition']),
            );
        } catch (err) {
            setExportError(TEXT.exportError);
        } finally {
            setExportLoading(false);
        }
    };

    const handleManualUpload = async (event) => {
        event.preventDefault();
        setManualError('');
        setManualResult(null);
        if (!manualFile) {
            setManualError(MANUAL_TEXT.missingFile);
            return;
        }
        try {
            setManualLoading(true);
            const formData = new FormData();
            formData.append('pdf', manualFile);
            const { data } = await api.post('/admin/debug/instructions-manual', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setManualResult(data);
        } catch (err) {
            setManualError(err.response?.data?.message || MANUAL_TEXT.error);
        } finally {
            setManualLoading(false);
        }
    };

    const handleLoadTemplateFiles = async () => {
        try {
            setTemplateFilesLoading(true);
            setTemplateFilesError('');
            const { data } = await api.get('/admin/debug/template-files');
            setTemplateFileReport(data);
        } catch (err) {
            setTemplateFilesError(TEMPLATE_FILES_TEXT.loadError);
            setTemplateFileReport(null);
        } finally {
            setTemplateFilesLoading(false);
        }
    };

    const handleTemplateRepairFileChange = (templateId, file) => {
        setTemplateRepairFiles((prev) => ({ ...prev, [templateId]: file || null }));
    };

    const handleRepairTemplateFile = async (event, templateId) => {
        event.preventDefault();
        setTemplateRepairError('');
        setTemplateRepairResult(null);
        const file = templateRepairFiles[templateId];
        if (!file) {
            setTemplateRepairError(TEMPLATE_FILES_TEXT.missingFile);
            return;
        }
        try {
            setTemplateRepairLoadingId(templateId);
            const formData = new FormData();
            formData.append('pdf', file);
            const { data } = await api.post(`/admin/debug/template-files/${templateId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setTemplateRepairResult(data);
            setTemplateRepairFiles((prev) => ({ ...prev, [templateId]: null }));
            await handleLoadTemplateFiles();
        } catch (err) {
            setTemplateRepairError(err.response?.data?.message || TEMPLATE_FILES_TEXT.error);
        } finally {
            setTemplateRepairLoadingId('');
        }
    };

    const handleLoadEmailJobs = async () => {
        try {
            setEmailJobsLoading(true);
            setEmailJobsError('');
            const { data } = await api.get('/admin/debug/email-jobs');
            setEmailJobs(Array.isArray(data) ? data : []);
        } catch (err) {
            setEmailJobsError(EMAIL_TEXT.error);
            setEmailJobs([]);
        } finally {
            setEmailJobsLoading(false);
        }
    };

    const handleRetryEmailJob = async (jobId) => {
        try {
            setEmailRetryingId(jobId);
            setEmailJobsError('');
            await api.post(`/admin/debug/email-jobs/${jobId}/requeue`);
            await handleLoadEmailJobs();
        } catch (err) {
            setEmailJobsError(err.response?.data?.message || EMAIL_TEXT.retryError);
        } finally {
            setEmailRetryingId('');
        }
    };

    const handleSetGlobalPassword = async (event) => {
        event.preventDefault();
        setGlobalPasswordError('');
        setGlobalPasswordResult(null);
        if (!globalPassword) {
            setGlobalPasswordError(GLOBAL_PASSWORD_TEXT.required);
            return;
        }
        if (globalPassword !== globalPasswordConfirm) {
            setGlobalPasswordError(GLOBAL_PASSWORD_TEXT.mismatch);
            return;
        }
        if (!window.confirm(GLOBAL_PASSWORD_TEXT.confirm)) return;

        try {
            setGlobalPasswordLoading(true);
            const { data } = await api.post('/admin/debug/users/global-password', {
                globalPassword,
            });
            setGlobalPassword('');
            setGlobalPasswordConfirm('');
            setGlobalPasswordResult(data);
        } catch (err) {
            setGlobalPasswordError(err.response?.data?.message || GLOBAL_PASSWORD_TEXT.error);
        } finally {
            setGlobalPasswordLoading(false);
        }
    };

    return (
        <div className="page page--admin-debug page--accent stack stack--lg">
            <header className="page__header">
                <h1 className="page__title">{TEXT.title}</h1>
            </header>

            <section className="card stack">
                <h2 className="card__title">{GLOBAL_PASSWORD_TEXT.title}</h2>
                <p className="card__meta">{GLOBAL_PASSWORD_TEXT.description}</p>
                <form className="stack" onSubmit={handleSetGlobalPassword}>
                    <label className="form-field">
                        <span className="form-field__label">{GLOBAL_PASSWORD_TEXT.password}</span>
                        <input
                            className="form-field__control"
                            type="password"
                            value={globalPassword}
                            onChange={(event) => setGlobalPassword(event.target.value)}
                            autoComplete="new-password"
                            required
                        />
                    </label>
                    <label className="form-field">
                        <span className="form-field__label">{GLOBAL_PASSWORD_TEXT.confirmPassword}</span>
                        <input
                            className="form-field__control"
                            type="password"
                            value={globalPasswordConfirm}
                            onChange={(event) => setGlobalPasswordConfirm(event.target.value)}
                            autoComplete="new-password"
                            required
                        />
                    </label>
                    <div className="admin-debug__actions">
                        <button
                            type="submit"
                            className="button button--danger"
                            disabled={globalPasswordLoading}
                        >
                            {globalPasswordLoading ? TEXT.loading : GLOBAL_PASSWORD_TEXT.action}
                        </button>
                    </div>
                </form>
                {globalPasswordError && <div className="alert alert--danger">{globalPasswordError}</div>}
                {globalPasswordResult && (
                    <div className="alert alert--success">
                        {GLOBAL_PASSWORD_TEXT.success} {globalPasswordResult.updatedCount || 0}
                    </div>
                )}
            </section>

            <section className="card stack">
                <h2 className="card__title">{TEXT.storageTitle}</h2>
                <p className="card__meta">{TEXT.storageDescription}</p>
                <div className="admin-debug__actions">
                    <button
                        type="button"
                        className="button button--light"
                        onClick={handleStorageCheck}
                        disabled={storageLoading}
                    >
                        {storageLoading ? TEXT.loading : TEXT.storageAction}
                    </button>
                </div>
                {storageError && <div className="alert alert--danger">{storageError}</div>}
                {storageHealth && (
                    <div className="stack">
                        <div className="card__meta">{TEXT.uploadRoot}: {storageHealth.uploadRoot}</div>
                        <div className="card__meta">
                            {TEXT.uploadProfile}: {storageHealth.profilePath} - {storageHealth.profileCheck?.ok ? TEXT.ok : TEXT.fail}
                            {storageHealth.profileCheck?.error ? ` (${storageHealth.profileCheck.error})` : ''}
                        </div>
                        <div className="card__meta">
                            {TEXT.uploadTemplates}: {storageHealth.templatePath} - {storageHealth.templateCheck?.ok ? TEXT.ok : TEXT.fail}
                            {storageHealth.templateCheck?.error ? ` (${storageHealth.templateCheck.error})` : ''}
                        </div>
                    </div>
                )}
            </section>

            <section className="card stack">
                <h2 className="card__title">{MANUAL_TEXT.title}</h2>
                <p className="card__meta">{MANUAL_TEXT.description}</p>
                <form className="stack" onSubmit={handleManualUpload}>
                    <label className="form-field">
                        <span className="form-field__label">{MANUAL_TEXT.file}</span>
                        <input
                            className="form-field__control"
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(event) => setManualFile(event.target.files?.[0] || null)}
                        />
                    </label>
                    <div className="admin-debug__actions">
                        <button
                            type="submit"
                            className="button button--primary"
                            disabled={manualLoading}
                        >
                            {manualLoading ? TEXT.loading : MANUAL_TEXT.action}
                        </button>
                    </div>
                </form>
                {manualError && <div className="alert alert--danger">{manualError}</div>}
                {manualResult && (
                    <div className="alert alert--success">
                        {MANUAL_TEXT.success} {manualResult.path}
                    </div>
                )}
            </section>

            <section className="card stack">
                <h2 className="card__title">{TEMPLATE_FILES_TEXT.title}</h2>
                <p className="card__meta">{TEMPLATE_FILES_TEXT.description}</p>
                <div className="admin-debug__actions">
                    <button
                        type="button"
                        className="button button--light"
                        onClick={handleLoadTemplateFiles}
                        disabled={templateFilesLoading}
                    >
                        {templateFilesLoading ? TEXT.loading : TEMPLATE_FILES_TEXT.action}
                    </button>
                </div>
                {templateFilesError && <div className="alert alert--danger">{templateFilesError}</div>}
                {templateRepairError && <div className="alert alert--danger">{templateRepairError}</div>}
                {templateRepairResult && (
                    <div className="alert alert--success">
                        {TEMPLATE_FILES_TEXT.success} {templateRepairResult.name} ({templateRepairResult.path})
                    </div>
                )}
                {templateFileReport && (
                    <div className="stack">
                        <div className="card__meta">
                            {TEMPLATE_FILES_TEXT.missingCount}: {templateFileReport.missingCount || 0}
                        </div>
                        {(templateFileReport.missingCount || 0) === 0 && (
                            <div className="alert alert--success">{TEMPLATE_FILES_TEXT.allFound}</div>
                        )}
                        {(templateFileReport.templates || [])
                            .filter((template) => !template.exists)
                            .map((template) => (
                                <div key={template.id} className="card stack">
                                    <div><strong>Missing</strong> - {template.name}</div>
                                    <div className="card__meta">Stored path: {template.filePath || '-'}</div>
                                    <div className="card__meta">Repair path: {template.repairPath || '-'}</div>
                                    <div className="alert alert--danger">{template.error || 'missing'}</div>
                                    <form className="stack" onSubmit={(event) => handleRepairTemplateFile(event, template.id)}>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEMPLATE_FILES_TEXT.choose}</span>
                                            <input
                                                className="form-field__control"
                                                type="file"
                                                accept="application/pdf,.pdf"
                                                onChange={(event) => handleTemplateRepairFileChange(template.id, event.target.files?.[0] || null)}
                                            />
                                        </label>
                                        <div className="admin-debug__actions">
                                            <button
                                                type="submit"
                                                className="button button--primary"
                                                disabled={templateRepairLoadingId === template.id}
                                            >
                                                {templateRepairLoadingId === template.id ? TEXT.loading : TEMPLATE_FILES_TEXT.upload}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ))}
                    </div>
                )}
            </section>

            <section className="card stack">
                <h2 className="card__title">{EMAIL_TEXT.title}</h2>
                <p className="card__meta">{EMAIL_TEXT.description}</p>
                <div className="admin-debug__actions">
                    <button
                        type="button"
                        className="button button--light"
                        onClick={handleLoadEmailJobs}
                        disabled={emailJobsLoading}
                    >
                        {emailJobsLoading ? TEXT.loading : EMAIL_TEXT.action}
                    </button>
                </div>
                {emailJobsError && <div className="alert alert--danger">{emailJobsError}</div>}
                {!emailJobsError && emailJobs.length === 0 && (
                    <div className="card__meta">{EMAIL_TEXT.empty}</div>
                )}
                {emailJobs.length > 0 && (
                    <div className="stack">
                        {emailJobs.map((job) => (
                            <div key={job.id} className="card stack">
                                <div><strong>{job.status}</strong> - {job.subject}</div>
                                <div className="card__meta">To: {job.toEmail}</div>
                                <div className="card__meta">Send at: {job.sendAt || '-'}</div>
                                <div className="card__meta">Attempts: {job.attempts || 0}</div>
                                <div className="admin-debug__email-body">
                                    {job.bodyText ? renderTextWithLinks(job.bodyText) : <span className="card__meta">No body.</span>}
                                </div>
                                {job.lastError && (
                                    <div className="alert alert--danger">{job.lastError}</div>
                                )}
                                {job.status === 'failed' && (
                                    <div className="admin-debug__actions">
                                        <button
                                            type="button"
                                            className="button button--primary"
                                            onClick={() => handleRetryEmailJob(job.id)}
                                            disabled={emailRetryingId === job.id}
                                        >
                                            {emailRetryingId === job.id ? TEXT.loading : EMAIL_TEXT.retry}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="card stack">
                <h2 className="card__title">{TEXT.templateTitle}</h2>
                <p className="card__meta">{TEXT.templateDescription}</p>
                <div className="admin-debug__actions">
                    <button
                        type="button"
                        className="button button--danger"
                        onClick={handleConvertFields}
                        disabled={convertLoading}
                    >
                        {convertLoading ? TEXT.loading : TEXT.convertAction}
                    </button>
                    <button
                        type="button"
                        className="button button--light"
                        onClick={handleConvertFieldsToAutoFont}
                        disabled={autoFontLoading}
                    >
                        {autoFontLoading ? TEXT.loading : TEXT.autoFontAction}
                    </button>
                    <button
                        type="button"
                        className="button button--light"
                        onClick={handleDownloadExports}
                        disabled={exportLoading}
                    >
                        {exportLoading ? TEXT.loading : TEXT.exportAction}
                    </button>
                </div>
                {convertError && <div className="alert alert--danger">{convertError}</div>}
                {autoFontError && <div className="alert alert--danger">{autoFontError}</div>}
                {exportError && <div className="alert alert--danger">{exportError}</div>}
                {convertResult && (
                    <div className="alert alert--success">
                        {TEXT.convertSuccess} {convertResult.updatedCount || 0}
                    </div>
                )}
                {autoFontResult && (
                    <div className="alert alert--success">
                        {TEXT.autoFontSuccess} {autoFontResult.updatedCount || 0}
                    </div>
                )}
            </section>
        </div>
    );
}
